/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CancellationCategoryProps {
  id: string;
  name: string;
  defaultCancellationReasonId: string | null;
  isVisibleInOps: boolean;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CancellationCategoryRecord extends Record<CancellationCategoryProps>({
  id: null as any,
  name: null as any,
  defaultCancellationReasonId: null as any,
  isVisibleInOps: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    defaultCancellationReasonId: string | null;
    isVisibleInOps: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CancellationCategoryRecord {
    if (json == null) {
      throw new Error('CancellationCategoryRecord.fromJS: parameter cannot be null');
    }
    return new CancellationCategoryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CancellationCategoryJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'CancellationCategoryJson.name'),
      defaultCancellationReasonId: json.defaultCancellationReasonId,
      isVisibleInOps: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isVisibleInOps', false),
        'CancellationCategoryJson.isVisibleInOps',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    defaultCancellationReasonId: string | null;
    isVisibleInOps: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CancellationCategoryRecord, Error[]] {
    if (json == null) {
      throw new Error('CancellationCategoryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CancellationCategoryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CancellationCategoryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'CancellationCategoryJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      defaultCancellationReasonId: tryOrDefault(
        () => json.defaultCancellationReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isVisibleInOps: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isVisibleInOps', false),
            'CancellationCategoryJson.isVisibleInOps',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CancellationCategoryProps) {
    super(props);
    if (props == null) {
      throw new Error('CancellationCategoryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CancellationCategoryViewModelBase implements CancellationCategoryProps {
  protected readonly _props: CancellationCategoryProps;
  private _json?: CancellationCategoryProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get defaultCancellationReasonId(): string | null {
    return this._props.defaultCancellationReasonId;
  }
  get isVisibleInOps(): boolean {
    return this._props.isVisibleInOps;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: CancellationCategoryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        defaultCancellationReasonId: this._props.defaultCancellationReasonId,
        isVisibleInOps: this._props.isVisibleInOps,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCancellationCategoryEntities = (state: AppState) =>
  state.entityStores.cancellationCategory.entities;

export const selectCancellationCategoryById = (id: string) => (state: AppState) =>
  state.entityStores.cancellationCategory.entities.get(id);

export const selectCancellationCategoryMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.cancellationCategory.meta.get(id);

export const selectCancellationCategoryAndMetadataById = (id: string) =>
  createSelector(
    selectCancellationCategoryById(id),
    selectCancellationCategoryMetadataById(id),
    (cancellationCategory, meta) => ({ cancellationCategory, meta }),
  );

export function selectCancellationCategoryEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCancellationCategoryEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CancellationCategoryRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCancellationCategoryEntitiesByName(name: string) {
  return createSelector(selectAllCancellationCategoryEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectCancellationCategoryEntitiesByDefaultCancellationReasonId(
  defaultCancellationReasonId: string,
) {
  return createSelector(selectAllCancellationCategoryEntities, (entities) =>
    entities
      .filter((e) => e.defaultCancellationReasonId === defaultCancellationReasonId)
      .valueSeq(),
  );
}

export function selectCancellationCategoryEntitiesByIsVisibleInOps(isVisibleInOps: boolean) {
  return createSelector(selectAllCancellationCategoryEntities, (entities) =>
    entities.filter((e) => e.isVisibleInOps === isVisibleInOps).valueSeq(),
  );
}
//#endregion Selectors
